import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentTemplate } from '../../../types/EducationHub';
import { useEducationHub } from '../../../contexts/EducationHubContext';
import styles from '../../../styles/EducationHub.module.css';
import LoadingSpinner from 'components/common/LoadingSpinner';

interface TemplatesSectionProps {
  userDetails: any;
}

const TemplatesSection: React.FC<TemplatesSectionProps> = ({ userDetails }) => {
  const { templates, templatesLoading: loading, fetchTemplates, downloadTemplate } = useEducationHub();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    fetchTemplates(userDetails.id);
  }, [fetchTemplates]);

  const filteredTemplates = templates.filter(template => {
    if (selectedCategory && template.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!template.doc_name.toLowerCase().includes(query)) return false;
    }
    return true;
  });

  const categories = Array.from(new Set(
    templates.map(t => t.category).filter((c): c is string => !!c)
  ));

  const handleDownload = async (template: DocumentTemplate, e: React.MouseEvent) => {
    e.stopPropagation();
    const fileName = `${template.doc_name}.${template.fileType.toLowerCase()}`;
    await downloadTemplate(template._id, { suggestedFilename: fileName, fileType: template.fileType });
  };

  const renderTemplateCard = (template: DocumentTemplate) => {
    
    return (
      <div key={template._id} className={styles.contentCard}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>
            {template.fileType === 'DOCX' && '📝 '}
            {template.fileType === 'PDF' && '📄 '}
            {template.fileType === 'XLSX' && '📊 '}
            {template.doc_name}
          </h3>
          <div className={styles.cardMeta}>
            <span>{template.fileType}</span>
          </div>
        </div>
        <div className={styles.cardBody}>
          {(template.category || template.subcategory) && (
            <div className={styles.documentTypeBox}>
              <div className={styles.documentTypeHeader}>
                <span className={styles.documentTypeIcon}>📋</span>
                <span className={styles.documentTypeLabel}>Category:</span>
              </div>
              <div className={styles.documentTypeName}>
                {template.category}
                {template.subcategory && ` > ${template.subcategory}`}
              </div>
            </div>
          )}
        </div>
        <div className={styles.cardFooter}>
          <div className={styles.buttonContainer}>
            <button
              className={styles.downloadBtn}
              onClick={(e) => handleDownload(template, e)}
              >
              📥 Download
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  if (templates.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>📋</div>
        <h3 className={styles.emptyTitle}>No Templates Available</h3>
        <p className={styles.emptyDescription}>
          Document templates will be added soon to help you with required uploads.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Search and Filters */}
      <div className={styles.filterContainer}>
        {/* Search Bar */}
        <div className={styles.searchBarWrapper}>
          <input
            type="text"
            placeholder="Search templates or document types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className={styles.categoryFilterWrapper}>
            <label className={styles.categoryLabel}>
              Category:
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={styles.categorySelect}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Clear Filters Button */}
        {(searchQuery || selectedCategory) && (
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('');
            }}
            className={styles.clearFiltersBtn}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Template Grid */}
      <div className={styles.contentGrid}>
        {filteredTemplates.map(renderTemplateCard)}
      </div>

      {filteredTemplates.length === 0 && (
        <div className={styles.emptyState}>
          <p>No templates found matching your {searchQuery ? 'search' : 'filters'}.</p>
        </div>
      )}
    </div>
  );
};

export default TemplatesSection;
